import { Fragment, type ReactNode } from 'react'
import {
  Card,
  CardContent,
  CardFooter,
  CardFooterContent,
  CardFooterDescription,
  CardHeader,
  CardHeaderActions,
  CardTitle,
} from '@/components/Card'
import styles from './JobInsightsCard.module.css'
import { Icon } from '@/components/Icon'
import { AnimatedIcon } from '@/components/AnimatedIcon'
import { RawButton } from '@/components/Button'
import { isRefreshDesign } from '@/infra/design'
import {
  Skeleton,
  SkeletonList,
  useSkeletonListContext,
} from '@/components/Skeleton'

export interface JobInsightsCardItem {
  prefixContent: string
  generatedResponse: string
}

export interface JobInsightsCardProps {
  actions?: ReactNode
  children?: ReactNode
}

export function JobInsightsCard({ actions, children }: JobInsightsCardProps) {
  return (
    <Card borderless>
      <CardHeader>
        <CardTitle>Insights</CardTitle>
        {actions && <CardHeaderActions>{actions}</CardHeaderActions>}
      </CardHeader>
      {children}
    </Card>
  )
}

export interface JobInsightsActionProps {
  label?: string
  isPending: boolean
  /**
   * Without it, the action hides its label and only shows that insights are
   * being generated.
   */
  onPress?: () => unknown
}

export function JobInsightsAction({
  label,
  isPending,
  onPress,
}: JobInsightsActionProps) {
  if (!onPress) {
    return (
      <div className={styles.insightsAction}>
        {isPending && <AnimatedIcon name="stars" />}
      </div>
    )
  }

  return (
    <RawButton
      className={styles.insightsAction}
      onPress={onPress}
      testId="job-insights-regenerate-or-generate-more-button"
    >
      {label}
      {isPending ? <AnimatedIcon name="stars" /> : <Icon name="stars" />}
    </RawButton>
  )
}

export interface JobInsightsContentProps {
  latestInsights: JobInsightsCardItem[]
  isLatestInsightsAvailable: boolean
  generatedTime: string | null
  isAnyIrrelevant: boolean
}

export function JobInsightsContent({
  latestInsights,
  isLatestInsightsAvailable,
  generatedTime,
  isAnyIrrelevant,
}: JobInsightsContentProps) {
  return (
    <>
      <CardContent
        autoHeight={isLatestInsightsAvailable}
        compactBottomPadding={isLatestInsightsAvailable}
        scroll
      >
        {isLatestInsightsAvailable ? (
          latestInsights.map((item, index: number) => (
            <Fragment key={index}>
              <div className={styles.divider}></div>
              <div className={styles.text}>
                {item.prefixContent}{' '}
                <span className={styles.textBold}>
                  {item.generatedResponse}
                </span>
              </div>
            </Fragment>
          ))
        ) : (
          <div className={styles.dividerBottom}></div>
        )}
      </CardContent>
      {isLatestInsightsAvailable && generatedTime !== null && (
        <CardFooter>
          <CardFooterContent>
            Last generated on {generatedTime}
          </CardFooterContent>
          {isAnyIrrelevant && (
            <CardFooterDescription>
              The Insights above might be stale.
            </CardFooterDescription>
          )}
        </CardFooter>
      )}
    </>
  )
}

/**
 * A placeholder for {@link JobInsightsContent} still loading.
 */
export function JobInsightsContentSkeleton() {
  // The classic design shows an empty card while loading.
  if (!isRefreshDesign) {
    return null
  }

  return (
    <CardContent autoHeight compactBottomPadding>
      <SkeletonList count={3}>
        <JobInsightSkeleton />
      </SkeletonList>
    </CardContent>
  )
}

function JobInsightSkeleton() {
  const { varyPercent } = useSkeletonListContext()
  return (
    <>
      <div className={styles.divider}></div>
      <div className={styles.text}>
        <Skeleton width={varyPercent(85)} />
      </div>
    </>
  )
}
